import { defineStore } from 'pinia'
import { getResources } from '@/resources/api'
import { filterResourcesByLanguage, hasNonEnglishLanguage } from '@/resources/resource_list'
import type { Resource } from '@/resources/resource_types'

const PREFERRED_LANGUAGE = 'english'

function distinctLanguages(resources: Resource[]): string[] {
  const languages = resources.map((resource) => resource.language).filter((language): language is string => !!language)
  return [...new Set(languages)]
}

function defaultLanguage(resources: Resource[]): string | undefined {
  const languages = distinctLanguages(resources)
  return languages.includes(PREFERRED_LANGUAGE) ? PREFERRED_LANGUAGE : languages[0]
}

function hasSameResources(cached: Resource[] | undefined, resourceUuids: string[]): boolean {
  return !!cached &&
    cached.length === resourceUuids.length &&
    cached.every((resource) => resourceUuids.includes(resource.uuid))
}

let latestRequestNumber = 0

/** The resources attached to a goal and to each of its objectives, keyed by the uuid they hang off. */
export const useGoalResourcesStore = defineStore('goalResources', {
  state: () => ({
    resourcesByContentId: {} as Record<string, Resource[]>,
    isFetchingByContentId: {} as Record<string, boolean>,
    selectedLanguageByContentId: {} as Record<string, string>,
    latestRequestNumberByContentId: {} as Record<string, number>
  }),

  getters: {
    resourcesById(state) {
      return (contentId: string): Resource[] => state.resourcesByContentId[contentId] ?? []
    },
    isFetchingResourcesById(state) {
      return (contentId: string): boolean => state.isFetchingByContentId[contentId] ?? false
    },
    selectedLanguageById(state) {
      return (contentId: string): string => state.selectedLanguageByContentId[contentId] ?? ''
    },
    languagesById(state) {
      return (contentId: string): string[] => distinctLanguages(state.resourcesByContentId[contentId] ?? []).sort()
    },
    hasNonEnglishLangById(state) {
      return (contentId: string): boolean => hasNonEnglishLanguage(state.resourcesByContentId[contentId] ?? [])
    },
    filteredResourcesById(state) {
      return (contentId: string): Resource[] =>
        filterResourcesByLanguage(state.resourcesByContentId[contentId] ?? [], state.selectedLanguageByContentId[contentId] ?? '')
    }
  },

  actions: {
    async fetchResources(contentId: string, resourceUuids: string[]): Promise<Resource[]> {
      const requestNumber = ++latestRequestNumber
      this.latestRequestNumberByContentId[contentId] = requestNumber
      const isSuperseded = () => this.latestRequestNumberByContentId[contentId] !== requestNumber
      if (!resourceUuids.length) {
        this.resourcesByContentId[contentId] = []
        this.isFetchingByContentId[contentId] = false
        return []
      }
      if (hasSameResources(this.resourcesByContentId[contentId], resourceUuids)) {
        this.isFetchingByContentId[contentId] = false
        return this.resourcesByContentId[contentId]
      }
      this.isFetchingByContentId[contentId] = true
      try {
        const resources = await getResources(resourceUuids)
        if (isSuperseded()) {
          return []
        }
        this.resourcesByContentId[contentId] = [...resources]
        this.selectDefaultLanguage(contentId, resources)
        return resources
      } catch (error) {
        console.error('Error fetching resources:', error)
        return []
      } finally {
        if (!isSuperseded()) {
          this.isFetchingByContentId[contentId] = false
        }
      }
    },

    selectDefaultLanguage(contentId: string, resources: Resource[]) {
      const language = defaultLanguage(resources)
      if (this.selectedLanguageByContentId[contentId] || !language) {
        return
      }
      this.selectedLanguageByContentId[contentId] = language
    },

    setSelectedLanguage(contentId: string, language: string) {
      this.selectedLanguageByContentId[contentId] = language
    }
  }
})
